import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useOrderContext } from '../OrderProvider';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import DataGrid from '@afosto/app-components-react/DataGrid';
import { invoices as invoicesApi } from '@afosto/order/api/Invoices';
import { invoiceFormatter } from './formatters';
import translations from './translations';
import Hidden from '@material-ui/core/Hidden';
import DataList from '@afosto/app-components-react/DataList';
import InvoiceListItem from '../invoiceListItem';

const fetchInvoices = orderId => async () => {
  try {
    const response = await invoicesApi.listInvoices({ orderId });
    const { data = [] } = response;

    return data;
  } catch (err) {
    return [];
  }
};

const InvoicesSection = ({ invoiceIds }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:invoices:read']);
  const { order } = useOrderContext();

  const dataSource = useDataGrid(fetchInvoices(order?.id), {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'number',
          name: intl.formatMessage({
            id: 'dataGrid.columns.invoices.number',
            defaultMessage: 'Number',
            description: 'The column name for column number',
          }),
          type: 'invoice',
        },
        {
          key: 'total',
          name: intl.formatMessage({
            id: 'dataGrid.columns.invoices.amount',
            defaultMessage: 'Amount',
            description: 'The column name for column amount',
          }),
          type: 'currency',
        },
        {
          key: 'is_paid',
          name: intl.formatMessage({
            id: 'dataGrid.columns.invoices.isPaid',
            defaultMessage: 'Paid',
            description: 'The column name for column is paid',
          }),
          type: 'boolean',
        },
        {
          key: 'paid_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.invoices.paidAt',
            defaultMessage: 'Paid at',
            description: 'The column name for column paid at',
          }),
          type: 'datetime',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.invoices.invoicedAt',
            defaultMessage: 'Invoiced at',
            description: 'The column name for column invoiced at',
          }),
          type: 'datetime',
        },
      ],
    },
  });
  const dataSourceRef = useRef(dataSource);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    dataSourceRef.current.reloadDataSource();
  }, [invoiceIds]);

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
      />
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={invoice => <InvoiceListItem invoice={invoice} key={invoice?.id} />}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid dataSource={dataSource} formatters={{ invoice: invoiceFormatter }} />
      </Hidden>
    </Card>
  );
};

InvoicesSection.propTypes = {
  invoiceIds: PropTypes.array,
};

InvoicesSection.defaultProps = {
  invoiceIds: [],
};

export default InvoicesSection;
